import { STYLE_PROPERTIES, type StyleProperty, type StyleValueDto, type ThemeStylesDto } from '@xshop/shared';
import { shrinkableTracks } from './gridTracks';
import { resolveStyleToken } from './resolveStyleToken';
import { cssPropertyName, formatStyleNumber } from './styleUnits';

// One stored property and one stored value, turned into the one declaration a stylesheet writes — or into
// the reason nothing is written.
//
// Two things are refused here rather than anywhere else, and both are the safety of the whole feature.
// A property `STYLE_PROPERTIES` does not name is dropped, so the closed list is what actually reaches a
// shopper rather than merely what the inspector draws. And a value that could close its own declaration —
// a brace, a semicolon, an at-rule, a comment — is dropped too, because the tree is compiled in the
// builder before it has ever been through the backend's schema, and a merchant must not be able to write
// CSS for anything but their own element.

/** Why one declaration was dropped, so the builder can say so rather than the page quietly losing it. */
export type StyleDropReason = 'unknown-property' | 'unknown-token' | 'unusable-number' | 'unsafe-value';

/** One dropped declaration: what was asked for, and why nothing was written for it. */
export type StyleDrop = { property: string; reason: StyleDropReason; token?: string };

/** A declaration, or the drop that stands in its place. Exactly one of the two is ever set. */
export type StyleDeclarationResult = { css: string | null; drop: StyleDrop | null };

type ResolvedValue = { ok: true; text: string } | { ok: false; reason: StyleDropReason };

const KNOWN_PROPERTIES: ReadonlySet<string> = new Set<string>(STYLE_PROPERTIES);

/** Anything that could end this declaration and begin one of its own; no honest value carries these. */
const CSS_BREAKOUT_RE = /[{}<>;@\\]|\/\*|\*\//;

/** The browser-side twin of `UNSAFE_STYLE_RE` in `element.schema.ts`, for values not yet through it. */
const CSS_UNSAFE_RE = /expression\s*\(|javascript:|behavior\s*:|url\s*\(\s*["']?\s*(?:javascript|data:text\/html)/i;

const MAX_VALUE_LENGTH = 400;

// Reports whether a resolved value can be written into a stylesheet without escaping its own declaration.
export function isSafeCssValue(value: string): boolean {
  if (value === '' || value.length > MAX_VALUE_LENGTH) return false;
  return !CSS_BREAKOUT_RE.test(value) && !CSS_UNSAFE_RE.test(value);
}

// Reads the token name off a style value, for the object form only; anything else is not a token at all.
function tokenName(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const token = (value as { token?: unknown }).token;
  return typeof token === 'string' && token !== '' ? token : null;
}

// Shapes the two properties whose stored value is not yet the CSS one: grid columns, and a bare image URL.
function shapeStyleValue(property: StyleProperty, value: string): string {
  if (property === 'gridColumns') return /^[0-9]+$/.test(value) ? `repeat(${value}, minmax(0, 1fr))` : shrinkableTracks(value);
  if (property !== 'backgroundImage') return value;
  const already = value === 'none' || /^[a-z-]+\(/i.test(value);
  return already ? value : `url("${value}")`;
}

// Names the palette colour a token is, or null for any other token: `primary` and `colors.primary` both name one.
function colourKey(name: string): string | null {
  const parts = name.split('.');
  const key = parts.length === 1 ? parts[0] : parts.length === 2 && parts[0] === 'colors' ? parts[1] : null;
  return key && /^[A-Za-z]+$/.test(key) ? key : null;
}

// Writes a resolved token as CSS: a palette colour as the page's variable, so a colour scheme can repaint it.
function tokenText(name: string, resolved: string): string {
  const key = colourKey(name);
  return key ? `var(--xe-${key}, ${resolved})` : resolved;
}

// Resolves one stored value to CSS text: a token against the store, a number through its unit, a literal as is.
function resolveValue(property: StyleProperty, value: StyleValueDto, theme: ThemeStylesDto): ResolvedValue {
  if (typeof value === 'number') {
    const text = formatStyleNumber(property, value);
    return text === null ? { ok: false, reason: 'unusable-number' } : { ok: true, text };
  }
  if (typeof value === 'string') return { ok: true, text: value };
  const name = tokenName(value);
  if (name === null) return { ok: false, reason: 'unsafe-value' };
  const resolved = resolveStyleToken(name, theme);
  return resolved === null ? { ok: false, reason: 'unknown-token' } : { ok: true, text: tokenText(name, resolved) };
}

// Turns one property and its stored value into the declaration to write, or the reason nothing is written.
export function styleDeclaration(property: string, value: StyleValueDto, theme: ThemeStylesDto): StyleDeclarationResult {
  if (!KNOWN_PROPERTIES.has(property)) return { css: null, drop: { property, reason: 'unknown-property' } };
  const known = property as StyleProperty;
  const resolved = resolveValue(known, value, theme);
  const token = tokenName(value) ?? undefined;
  if (!resolved.ok) return { css: null, drop: { property, reason: resolved.reason, ...(token ? { token } : {}) } };
  const shaped = shapeStyleValue(known, resolved.text.trim());
  if (!isSafeCssValue(shaped)) return { css: null, drop: { property, reason: 'unsafe-value', ...(token ? { token } : {}) } };
  return { css: `${cssPropertyName(known)}:${shaped}`, drop: null };
}
